import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import '../css/skeleton.css';
import '../css/normalize.css';
import '../css/components.css';
import profile from '../assets/profile.jpg';
import logo from '../assets/logo.gltf';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

function Header({ gridActive, setGridActive }) {
  const mountRef = useRef(null);
  const modelRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!mountRef.current) return;
    const container = mountRef.current;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Get current container size dynamically
    const width = container.clientWidth || 360;
    const height = container.clientHeight || 340;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    camera.position.z = 5;
    cameraRef.current = camera;

    // Renderer setup (transparent background, sitting directly on page)
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.margin = '0 auto';
    rendererRef.current = renderer;

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    container.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.3);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 2.0);
    directionalLight.position.set(5, 10, 7);
    scene.add(directionalLight);

    const accentLight = new THREE.PointLight(0xffffff, 3.0, 12);
    accentLight.position.set(-4, -4, 3);
    scene.add(accentLight);

    // Load GLTF model
    const loader = new GLTFLoader();
    loader.load(
      logo,
      function (gltf) {
        const box = new THREE.Box3().setFromObject(gltf.scene);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Center model geometry
        gltf.scene.position.x = -center.x;
        gltf.scene.position.y = -center.y;
        gltf.scene.position.z = -center.z;

        // Scale model up so it fills most of the available section space as a bold hero anchor
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
        cameraZ *= 0.92; // Optimized zoom to fill available space without clipping
        camera.position.z = cameraZ;
        camera.updateProjectionMatrix();

        modelRef.current = gltf.scene;
        scene.add(gltf.scene);
      },
      undefined,
      function (error) {
        console.error('Error loading GLTF model:', error);
      }
    );

    // Dynamic Resize handling
    const handleResize = () => {
      if (!container || !rendererRef.current || !cameraRef.current) return;
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight || 340;

      cameraRef.current.aspect = newWidth / newHeight;
      cameraRef.current.updateProjectionMatrix();

      rendererRef.current.setSize(newWidth, newHeight);
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(container);

    // Mouse interactive tilt inertia across screen
    let targetRotationX = 0;
    let targetRotationY = 0;
    let currentRotationX = 0;
    let currentRotationY = 0;

    const onMouseMove = (event) => {
      const x = (event.clientX / window.innerWidth) * 2 - 1;
      const y = -(event.clientY / window.innerHeight) * 2 + 1;
      targetRotationY = x * 0.45;
      targetRotationX = -y * 0.35;
    };

    window.addEventListener('mousemove', onMouseMove);

    // Animation loop
    let animId;
    function animate() {
      animId = requestAnimationFrame(animate);
      if (modelRef.current) {
        modelRef.current.rotation.y += 0.009;

        currentRotationX += (targetRotationX - currentRotationX) * 0.05;
        currentRotationY += (targetRotationY - currentRotationY) * 0.05;

        modelRef.current.rotation.x = currentRotationX;
      }
      renderer.render(scene, camera);
    }
    animate();

    return () => {
      cancelAnimationFrame(animId);
      resizeObserver.disconnect();
      window.removeEventListener('mousemove', onMouseMove);
      if (rendererRef.current && rendererRef.current.domElement) {
        rendererRef.current.dispose();
      }
    };
  }, []);

  const copyEmail = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText('info@t4nx.xyz');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <header className="Header-wrapper">
      {/* Top Meta Bar */}
      <div className="meta-bar container">
        <div className="meta-tag">
          <span className="accent-dot"></span>
          @thankyoudom
        </div>
        <div className="meta-actions">
          <button
            className={`grid-toggle-btn ${gridActive ? 'active' : ''}`}
            onClick={() => setGridActive(!gridActive)}
            title="Toggle Structural Grid Overlay"
          >
            GRID: {gridActive ? '[ON]' : '[OFF]'}
          </button>
          <span className="mono-dim">{'// SPEC 01.A'}</span>
        </div>
      </div>

      {/* Main Profile Header Object */}
      <div className="Header container">
        <div className="profile-header industrial-card">
          {/* Corner Ticks */}
          <div className="corner-tick top-left">+</div>
          <div className="corner-tick top-right">+</div>
          <div className="corner-tick bottom-left">+</div>
          <div className="corner-tick bottom-right">+</div>

          <div className="profile-info">
            <div className="editorial-badge">
              <span className="accent-text">&ldquo;IDENTITY&rdquo;</span>
              <span className="mono-sub">REF. 2026</span>
            </div>
            <h1>
              Dominique<span className="accent-period">.</span>
            </h1>
            <p className="subtitle-mono">
              <span className="tag-bracket">[</span>Producer / Designer
              <span className="tag-bracket">]</span>
            </p>
            <div className="contact-action">
              <a
                className="email-link"
                href="mailto:info@t4nx.xyz"
                onClick={copyEmail}
                title="Click to copy email address"
              >
                <i className="fas fa-envelope" aria-hidden="true"></i>
                <span className="email-text">info@t4nx.xyz</span>
                <span className="action-tag">
                  {copied ? '[COPIED!]' : '[COPY]'}
                </span>
              </a>
            </div>
          </div>

          <div className="profile-image-container">
            <div className="profile-image">
              <img src={profile} alt="Dominique Profile" />
            </div>
            <div className="image-spec-label">FIG 01 — AVATAR</div>
          </div>
        </div>
      </div>

      {/* Frameless Hero 3D Canvas Section */}
      <div className="container hero-3d-section">
        <div ref={mountRef} className="hero-3d-canvas"></div>
      </div>
    </header>
  );
}

export default Header;